// Package 4, part A (owner, 7. 10. 2026): the guided step on screen. The screen dims and only the element to tap stays
// live (its `data-guide`), with one sentence; the step ends with the player's real action on the server. The stage shown
// is the furthest one whose element is on screen (the map's pin once the map is open, Send once the order sheet is), so a
// refresh or another device picks up where the player is. A step that needs the survivor at camp never makes anyone
// wait: "Come back when Sha returns." and the game stays free. `Skip` (in the corner, with a confirm) ends a step for
// good. Nothing shows while an event or an encounter asks. 10. 10. 2026 (owner, deploy7): only the first session is
// guided; the runs and the attributes open with a small popup that explains them (`Got it`, then the stages).

import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GUIDED_STEPS, type GuidedDTO, type GuidedStepDef } from '@lastday/shared';
import { api } from '@/api/client';
import { qk, useMe } from '@/api/hooks';
import { Button } from '@/components/ui';
import { t } from '@/i18n';

const PAD = 6;

/**
 * 9. 10. 2026: the open modal on top (a Sheet, the item on a phone, a building: `role="dialog"` with `aria-modal`), or
 * null. Everything outside it is under it.
 */
function topModal(): HTMLElement | null {
  const all = document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal]');
  return all.length ? all[all.length - 1] : null;
}

/**
 * The first element on screen for a stage's targets (a hidden one, e.g. the side menu on a phone, does not count; 9. 10.
 * 2026: nor one under an open modal, `modal`).
 */
function findTarget(targets: string[], item: string | null, modal: HTMLElement | null = null): HTMLElement | null {
  for (const raw of targets) {
    const id = raw.replace('{item}', item ?? '');
    for (const el of Array.from(document.querySelectorAll<HTMLElement>(`[data-guide~="${CSS.escape(id)}"]`))) {
      if (modal && !modal.contains(el)) continue;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return el;
    }
  }
  return null;
}

/**
 * The furthest stage whose element is on screen, and the element. 9. 10. 2026: an `untilTap` stage on screen that was not
 * tapped yet comes first (the earliest of them), so one screen explains one thing at a time.
 */
function activeStage(def: GuidedStepDef, item: string | null, tapped: ReadonlySet<number> = new Set()): { i: number; el: HTMLElement | null; close?: boolean } {
  const modal = topModal();
  for (let i = 0; i < def.stages.length; i++) {
    if (!(def.stages[i].untilTap || def.stages[i].untilDone) || tapped.has(i)) continue;
    const el = findTarget(def.stages[i].target, item, modal);
    if (el) return { i, el };
  }
  for (let i = def.stages.length - 1; i >= 0; i--) {
    const el = findTarget(def.stages[i].target, item, modal);
    if (el) return { i, el };
  }
  // 9. 10. 2026: the step's element is there, but under an open sheet: the sheet's Close first.
  if (modal)
    for (let i = def.stages.length - 1; i >= 0; i--)
      if (findTarget(def.stages[i].target, item)) return { i, el: modal.querySelector<HTMLElement>('[data-guide~="sheet-close"]'), close: true };
  return { i: 0, el: null };
}

type Rect = { top: number; left: number; width: number; height: number };

/**
 * 9. 10. 2026: the element is out of the free part of the screen (above it, below it or under the phone's tab bar). One
 * taller than that part never counts (scrolling would not help).
 */
function outOfView(r: DOMRect): boolean {
  const bar = document.querySelector<HTMLElement>('[data-testid="tab-bar"]')?.getBoundingClientRect();
  const bottom = bar && bar.height > 0 ? bar.top : window.innerHeight;
  return r.height < bottom && (r.top < 0 || r.bottom > bottom);
}

const sameRect = (a: Rect | null, b: Rect | null) => !!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;

export function GuidedOverlay() {
  const me = useMe().data?.survivor;
  const g = me?.guided ?? null;
  // An event or an encounter asks first; the step comes back after.
  const busy = !!me?.pendingEncounterId || !!me?.pendingEventExpeditionId;
  if (!me || !g || busy) return null;
  return <GuidedStep key={`${g.id}:${g.item ?? ''}`} g={g} name={me.name} />;
}

function GuidedStep({ g, name }: { g: GuidedDTO; name: string }) {
  const def = GUIDED_STEPS[g.id];
  const qc = useQueryClient();
  // `close` (9. 10. 2026): the stage's element is under an open sheet; the ring is on the sheet's Close.
  const [stage, setStage] = useState<{ i: number; rect: Rect | null; close?: boolean }>({ i: 0, rect: null });
  const [confirm, setConfirm] = useState(false);
  const [hidden, setHidden] = useState(false);
  const seen = useRef(false);
  const elRef = useRef<HTMLElement | null>(null);
  const lastStage = useRef(-1);
  // 9. 10. 2026: the `untilTap` stages the player tapped (this step, this screen; 10. 10. 2026: `untilDone` by Done).
  const tapped = useRef(new Set<number>());
  // 9. 10. 2026: the player scrolled by hand in this stage (the target is then left where they put it).
  const handScrolled = useRef(false);
  // 9. 10. 2026: the ring is on an open sheet's Close (a tap there is not the stage's tap).
  const closing = useRef(false);

  // Follow the element every frame (it scrolls, the sheet slides in, the page changes).
  useLayoutEffect(() => {
    if (g.waiting) return;
    let raf = 0;
    const tick = () => {
      const a = activeStage(def, g.item, tapped.current);
      elRef.current = a.el;
      const changed = lastStage.current !== a.i || closing.current !== !!a.close;
      closing.current = !!a.close;
      const r = a.el?.getBoundingClientRect();
      const rect = r ? { top: Math.round(r.top), left: Math.round(r.left), width: Math.round(r.width), height: Math.round(r.height) } : null;
      setStage((s) => (s.i === a.i && !!s.close === !!a.close && (sameRect(s.rect, rect) || (!s.rect && !rect)) ? s : { i: a.i, rect, close: a.close }));
      if (a.el && changed) {
        lastStage.current = a.i;
        handScrolled.current = false;
        a.el.scrollIntoView({ block: 'center', inline: 'nearest' });
      } else if (a.el && r && !handScrolled.current && outOfView(r)) {
        // The page filled in above it and pushed it away (under the tab bar on a phone): back to the middle.
        a.el.scrollIntoView({ block: 'center', inline: 'nearest' });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [def, g.item, g.waiting]);

  // The first showing counts the step's time from here (once).
  useEffect(() => {
    if (g.waiting || seen.current || !stage.rect || g.intro) return;
    seen.current = true;
    void api.post('/guided/seen', { step: g.id }).catch(() => {});
  }, [g.id, g.waiting, stage.rect]);

  // 9. 10. 2026: scrolling by hand (wheel, finger, keys) leaves the target where the player put it.
  useEffect(() => {
    const onHand = () => {
      handScrolled.current = true;
    };
    const evs = ['wheel', 'touchmove', 'keydown'] as const;
    for (const ev of evs) window.addEventListener(ev, onHand, { passive: true });
    return () => {
      for (const ev of evs) window.removeEventListener(ev, onHand);
    };
  }, []);

  // 9. 10. 2026: a tap inside an `untilTap` stage's element moves on to the next stage (after the tap did its own work).
  useEffect(() => {
    if (g.waiting) return;
    const onClick = (e: MouseEvent) => {
      const i = lastStage.current;
      const el = elRef.current;
      if (i < 0 || !def.stages[i]?.untilTap || !el || closing.current) return;
      if (e.target instanceof Node && el.contains(e.target)) setTimeout(() => tapped.current.add(i), 0);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [def, g.waiting]);

  // A tap step ends with a tap on its last element (the contract board, a choice on the order sheet).
  useEffect(() => {
    if (!def.byTap || g.waiting) return;
    const onClick = (e: MouseEvent) => {
      const el = elRef.current;
      if (!el || lastStage.current !== def.stages.length - 1 || closing.current) return;
      if (e.target instanceof Node && el.contains(e.target)) void api.post('/guided/tap', { step: g.id }).then(() => qc.invalidateQueries({ queryKey: qk.me }), () => {});
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [def, g.id, g.waiting, qc]);

  const skip = () => api.post('/guided/skip', { step: g.id }).then(() => qc.invalidateQueries({ queryKey: qk.me }), () => {});

  // 10. 10. 2026 (owner): an `untilDone` stage ends with the sentence's Done (the next stage on the same screen comes).
  const doneStage = () => {
    tapped.current.add(stage.i);
  };

  // 10. 10. 2026 (owner): the step's intro first (what the runs or the attributes are); `Got it` counts as its showing.
  if (g.intro && def.intro && !g.waiting) {
    const read = () => {
      seen.current = true;
      void api.post('/guided/seen', { step: g.id }).then(() => qc.invalidateQueries({ queryKey: qk.me }), () => {});
    };
    return (
      <div className="pointer-events-auto fixed inset-0 z-[90] flex items-center justify-center bg-bg/75 px-4" data-testid="guided-intro" data-step={g.id}>
        <div role="dialog" aria-labelledby="guided-intro-title" className="flex w-full max-w-[380px] flex-col gap-2.5 rounded-[8px] border border-border bg-surface p-4 shadow-float">
          <span id="guided-intro-title" className="display text-[19px] font-semibold">
            {def.intro.title}
          </span>
          {confirm ? (
            <>
              <span className="text-[15px] leading-5">{t('common.guided.skipAsk')}</span>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => void skip()} data-testid="guided-skip-yes">
                  {t('common.guided.skipYes')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
                  {t('common.guided.keep')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <span className="text-[15px] leading-5" data-testid="guided-intro-text">
                {def.intro.text}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={read} data-testid="guided-intro-ok">
                  {t('common.guided.gotIt')}
                </Button>
                {/* The first session's Skip stays (owner, 10. 10. 2026). */}
                <Button size="sm" variant="ghost" onClick={() => setConfirm(true)} data-testid="guided-skip">
                  {t('common.guided.skip')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Waiting for the survivor: a note, and the game stays free.
  if (g.waiting) {
    if (hidden) return null;
    return (
      <div role="status" data-testid="guided-waiting" className="pointer-events-auto fixed inset-x-4 bottom-[88px] z-[45] mx-auto flex max-w-[420px] items-center gap-3 rounded-[8px] border border-border bg-surface p-3 shadow-float lg:bottom-6">
        <span className="min-w-0 flex-1 text-[14px] leading-5">{t('common.guided.waiting', { name })}</span>
        <Button size="sm" variant="ghost" onClick={() => setHidden(true)}>
          {t('common.guided.ok')}
        </Button>
      </div>
    );
  }

  // 9. 10. 2026: under an open sheet: "Close this to go on." with a ring round its Close, nothing dimmed.
  const text = stage.close ? t('common.guided.closeFirst') : (def.stages[stage.i]?.text ?? def.stages[0].text);
  // A screen of many choices (the tree, the attributes) or no element on screen: the sentence only, at the bottom.
  const r = def.stages[stage.i]?.soft && !stage.close ? null : stage.rect;
  const soft = !r || !!stage.close;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const hole = r ? { top: r.top - PAD, left: r.left - PAD, width: r.width + 2 * PAD, height: r.height + 2 * PAD } : null;
  // The sentence sits below the element, or above it when the element is low on the screen.
  const below = !hole || hole.top + hole.height / 2 < vh / 2;
  const bubbleW = Math.min(340, vw - 32);
  const bubbleStyle: CSSProperties = hole
    ? {
        width: bubbleW,
        left: Math.max(16, Math.min(vw - 16 - bubbleW, hole.left + hole.width / 2 - bubbleW / 2)),
        ...(below ? { top: Math.min(vh - 140, hole.top + hole.height + 12) } : { bottom: Math.max(16, vh - hole.top + 12) }),
      }
    : { width: bubbleW, left: (vw - bubbleW) / 2, bottom: 96 };
  const shade = 'pointer-events-auto fixed bg-bg/75';
  return (
    <div className="pointer-events-none fixed inset-0 z-[90]" data-testid="guided" data-step={g.id} data-stage={stage.i} data-close={stage.close || undefined}>
      {!soft && hole && (
        <>
          <div className={shade} style={{ top: 0, left: 0, width: vw, height: Math.max(0, hole.top) }} />
          <div className={shade} style={{ top: hole.top + hole.height, left: 0, width: vw, height: Math.max(0, vh - hole.top - hole.height) }} />
          <div className={shade} style={{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height }} />
          <div className={shade} style={{ top: hole.top, left: hole.left + hole.width, width: Math.max(0, vw - hole.left - hole.width), height: hole.height }} />
          <div className="fixed rounded-[8px] border-2 border-text" style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }} />
        </>
      )}
      {stage.close && hole && <div className="fixed rounded-[8px] border-2 border-text" data-testid="guided-close-ring" style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }} />}
      <div role="dialog" aria-live="polite" data-testid="guided-text" className="pointer-events-auto fixed rounded-[8px] border border-border bg-surface p-3 text-[15px] leading-5 shadow-float" style={bubbleStyle}>
        {confirm ? (
          <div className="flex flex-col gap-2.5">
            <span>{t('common.guided.skipAsk')}</span>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="secondary" onClick={() => void skip()} data-testid="guided-skip-yes">
                {t('common.guided.skipYes')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
                {t('common.guided.keep')}
              </Button>
            </div>
          </div>
        ) : def.stages[stage.i]?.untilDone && !stage.close ? (
          <div className="flex flex-col gap-2.5">
            <span>{text}</span>
            <Button size="sm" variant="secondary" onClick={doneStage} className="self-start" data-testid="guided-done">
              {t('common.guided.done')}
            </Button>
          </div>
        ) : (
          <span>{text}</span>
        )}
      </div>
      {!confirm && (
        <button type="button" onClick={() => setConfirm(true)} data-testid="guided-skip" className="pointer-events-auto fixed right-4 top-4 rounded-[6px] border border-border bg-surface px-3 py-1.5 text-[13px] font-semibold text-muted hover:text-text">
          {t('common.guided.skip')}
        </button>
      )}
    </div>
  );
}
